// =============================================================================
// useChatGptState.js — shared ChatGPT (OpenAI) connection state
// =============================================================================
// Single source of truth for "is ChatGPT usable?" across the UI:
//   • Settings → Automations (the ChatGPT card in FangateSettings.vue) connects
//     a key, which creates the `openai` integration hook server-side.
//   • The chat composer (FangateChatArea.vue) shows its "Suggest reply" button
//     only when ChatGPT is connected AND not locally paused.
//
// CONNECTED is derived from the Vuex `integrations` store — the `openai` app
// record with at least one hook. The raw key lives ONLY server-side in the
// hook's settings; we never persist it in the browser. The store must be
// hydrated once (FangateLayout dispatches `integrations/get` on mount).
//
// PAUSED is a small client-only convenience: connected, but the agent has
// temporarily switched the Suggest-reply button off without deleting the key.
// Persisted in localStorage, shared in-tab via a module-level ref.
// =============================================================================
import { ref, computed } from 'vue';
import { useStore } from 'vuex';

const PAUSE_KEY = 'cc_chatgpt_paused:v1';

// Module-level singleton so the pause toggle in Settings is reflected live in
// the chat composer without prop drilling.
const paused = ref((() => {
  try { return localStorage.getItem(PAUSE_KEY) === '1'; } catch (_) { return false; }
})());

const setPaused = value => {
  paused.value = !!value;
  try { localStorage.setItem(PAUSE_KEY, value ? '1' : '0'); } catch (_) { /* storage unavailable */ }
};

export function useChatGptState() {
  const store = useStore();

  const openaiApp = computed(
    () => (store.getters['integrations/getAppIntegrations'] || []).find(app => app.id === 'openai') || null
  );
  const openaiHook = computed(() => openaiApp.value?.hooks?.[0] || null);

  // Connected = a saved OpenAI integration hook exists for this account.
  const connected = computed(() => !!openaiHook.value);

  // The single gate the rest of the UI consults.
  const isChatGptActive = computed(() => connected.value && !paused.value);

  // Masked preview of the stored key (display only — full key stays server-side).
  const keyMasked = computed(() => {
    const key = openaiHook.value?.settings?.api_key || '';
    if (!key) return '';
    return key.length > 12 ? `${key.slice(0, 5)}••••••${key.slice(-4)}` : '••••••';
  });

  return {
    openaiApp,
    openaiHook,
    connected,
    isChatGptActive,
    keyMasked,
    paused,
    setPaused,
  };
}
